import {ContractDto, MaterialDto} from '../../options/contract.types'
import {RetarderConstraintsDto} from '../../options/retarder.types'
import {OptionsDto} from '../../options/types'
import {PumpsConstraintsDto} from '../../options/unloadingMethods.types'
import {BusinessDay, BusinessHours} from '../../options/workCalendar.types'

import {
  OrderIntakeOptionsDto,
  OrderIntakeMaterialDto,
  OrderIntakeContractDto,
  OrderIntakeSiteDto,
  LegacyPumpConstraintsDto,
  ConfigurationDto,
  OrderIntakePlantDto
} from './types'

/**
 * Maps Order Intake API options to internal OrderProcessOptions format.
 *
 * This function transforms the multi-contract order intake API structure
 * into the format expected by the order process package.
 *
 * @param options - Raw API response for order intake
 * @param orgUnitId - Optional organization unit ID for filtering
 * @param countryFeatures - Country-specific feature toggles
 * @returns Mapped options in OrderProcessOptions format
 */
export const mapOrderIntakeOptions = (
  options: OrderIntakeOptionsDto,
  orgUnitId?: string,
  countryFeatures?: {
    hasConcreteUsage?: boolean
    hasDriverInstructions?: boolean
  }
): OptionsDto => {
  const {pumpConstraints, pumps, retarder, orderCreateNotes, ...restConstraints} =
    options.constraints

  return {
    constraints: {
      ...restConstraints,
      notes: orderCreateNotes ?? {isEnabled: false},
      // Handle both pump structures - prefer pumps if available, otherwise map pumpConstraints
      pumps:
        pumps ??
        (pumpConstraints
          ? mapPumpConstraints(pumpConstraints, options.contracts ?? [])
          : undefined),
      retarder: mapRetarder(retarder),
      // Country-specific features
      usage: {
        isEnabled: countryFeatures?.hasConcreteUsage ?? false
      },
      driverInstructions: {
        isEnabled: countryFeatures?.hasDriverInstructions ?? false
      }
    },
    contracts: mapContracts(options, orgUnitId),
    workCalendar: mapWorkCalendar(options, orgUnitId)
  }
}

/**
 * Maps work calendar configuration from order intake options.
 */
const mapWorkCalendar = (options: OrderIntakeOptionsDto, orgUnitId?: string) => {
  const effectiveOrgUnitId = orgUnitId || (options.contracts ?? [])[0]?.orgUnitId || ''

  const configuration = findConfiguration(options.configurations ?? [], effectiveOrgUnitId)

  return {
    businessHours: configuration?.allowedBusinessHours as BusinessHours,
    businessDays: (configuration?.allowedBusinessDays || []) as BusinessDay[],
    dateExceptions: {
      publicHolidays: configuration?.dateExceptions?.publicHolidays,
      excludedDates: configuration?.dateExceptions?.weekends
    }
  }
}

/**
 * Maps contracts with their materials and shipping addresses.
 */
const mapContracts = (options: OrderIntakeOptionsDto, orgUnitId?: string): ContractDto[] => {
  // Filter contracts by orgUnitId if provided
  const contracts = orgUnitId
    ? (options.contracts ?? []).filter((c) => c.orgUnitId === orgUnitId)
    : (options.contracts ?? [])

  // Create lookup maps
  const sitesByContract = createSitesByContractMap(options.sites ?? [])
  const plantsByNumber = createPlantsByNumberMap(options.plants ?? [])

  return contracts
    .map((contract) => {
      const site = sitesByContract.get(contract.contractNumber)
      if (!site) return null // Skip if no site found

      return mapContract(contract, plantsByNumber)
    })
    .filter((c): c is ContractDto => c !== null)
}

/**
 * Maps a single contract to ContractDto format.
 */
const mapContract = (
  contract: OrderIntakeContractDto,
  plantsByNumber: Map<string, string>
): ContractDto => {
  return {
    contractNumber: contract.contractNumber,
    orgUnitId: contract.orgUnitId,
    customerNumber: contract.customerNumber,
    contractDescription: contract.contractDescription,
    materials: contract.materials
      .filter((material) => material.shippingType === 'Deliver')
      .map(
        (material): MaterialDto => ({
          materialId: material.materialId,
          materialNumber: material.materialNumber,
          contractItemPositionNumber: material.contractItemPositionNumber,
          shippingType: material.shippingType,
          materialVariations: material.materialVariations ?? null,
          poNumber: material.poNumber,
          materialDescription: material.materialDescription,
          customerMaterialDescription: material.customerMaterialDescription,
          plantId: plantsByNumber.get(material.plantNumber ?? '') ?? null,
          plantNumber: material.plantNumber
        })
      ),
    dateRange: {
      from: contract.contractValidityPeriods.deliver.contractValidityStartDate,
      to: contract.contractValidityPeriods.deliver.contractValidityEndDate
    },
    contractStartDate: contract.contractValidityPeriods.deliver.contractStartDate,
    contractEndDate: contract.contractValidityPeriods.deliver.contractEndDate,
    zkPartner: undefined,
    dispatchPhoneNumbers: null,
    salesOffice: contract.salesOffice,
    marketArea: contract.marketArea
  }
}

/**
 * Maps alternative pump constraints structure to standard PumpsConstraintsDto.
 */
const mapPumpConstraints = (
  pumpConstraints: LegacyPumpConstraintsDto,
  contracts: OrderIntakeContractDto[]
): PumpsConstraintsDto => {
  if (
    !pumpConstraints.isEnabled ||
    !pumpConstraints.defaultSettings ||
    !pumpConstraints.hoseOptions ||
    !pumpConstraints.units ||
    pumpConstraints.availablePumpSizes?.length === 0
  ) {
    return {isEnabled: false}
  }

  const defaultPump =
    pumpConstraints.availablePumpSizes.find(
      (x) => x.size === pumpConstraints.defaultSettings.pumpSize
    ) ?? pumpConstraints.availablePumpSizes[0]

  const materials = contracts.flatMap((c) => c.materials)

  return {
    isEnabled: true,
    pumpTypes: {
      sizeUom: pumpConstraints.units.pumpSize,
      default: defaultPump.code,
      values: pumpConstraints.availablePumpSizes.map((pump) => ({
        number: pump.code,
        size: pump.size,
        ...calculateAutoConfirm(pump, materials)
      }))
    },
    hoseLength: {
      min: pumpConstraints.hoseOptions.minLength,
      max: pumpConstraints.hoseOptions.maxLength,
      step: pumpConstraints.hoseOptions.stepLength,
      uom: pumpConstraints.units.hoseSize,
      default: pumpConstraints.defaultSettings.hoseLength
    },
    defaultSettings: {
      discharge: pumpConstraints.defaultSettings.discharge,
      isPossibleToCleanPumpOnSite: pumpConstraints.defaultSettings.isPossibleToCleanPumpOnSite,
      canLargerPumpFit: pumpConstraints.defaultSettings.canLargerPumpFit
    }
  }
}

/**
 * Calculates auto-confirm configuration for a pump based on lead time and material availability.
 */
const calculateAutoConfirm = (
  pumpItem: {code: string; leadTime: number},
  materials: OrderIntakeMaterialDto[]
): {autoConfirmable: false} | {autoConfirmable: true; autoConfirmableAfter: string} => {
  // If pump requires lead time, it's not auto-confirmable
  if (pumpItem.leadTime > 0) {
    return {autoConfirmable: false}
  }

  // Check if pump exists as a material (for auto-confirm date calculation)
  const pumpMaterial = materials.find((m) => m.materialNumber === pumpItem.code)
  if (!pumpMaterial) {
    return {autoConfirmable: false}
  }

  // Pump is available immediately
  return {
    autoConfirmable: true,
    autoConfirmableAfter: new Date().toISOString()
  }
}

/**
 * Maps retarder constraints to standard format.
 */
const mapRetarder = (
  retarder: OrderIntakeOptionsDto['constraints']['retarder']
): RetarderConstraintsDto => {
  // Handle discriminated union - check if it's already in the correct format
  if (retarder && 'isEnabled' in retarder && typeof retarder.isEnabled === 'boolean') {
    if (!retarder.isEnabled) {
      return {isEnabled: false}
    }

    // Already in correct format
    if ('min' in retarder) {
      return retarder as RetarderConstraintsDto
    }

    // Old format with different property names
    if ('minRetarder' in retarder) {
      return {
        isEnabled: true,
        min: (retarder as any).minRetarder,
        max: (retarder as any).maxRetarder,
        step: (retarder as any).stepRetarder,
        uom: (retarder as any).retarderUom
      }
    }
  }

  return {isEnabled: false}
}

/**
 * Helper: Finds configuration for a given sales organization.
 */
const findConfiguration = (
  configurations: ConfigurationDto[],
  salesOrganization: string
): ConfigurationDto | undefined => {
  return (
    configurations.find((config) => config.salesOrganization === salesOrganization) ||
    configurations[0]
  )
}

/**
 * Helper: Creates a map of sites by contract number for fast lookup.
 */
const createSitesByContractMap = (sites: OrderIntakeSiteDto[]): Map<string, OrderIntakeSiteDto> => {
  const map = new Map<string, OrderIntakeSiteDto>()
  sites.forEach((site) => {
    site.contractNumbers.forEach((contractNumber) => {
      map.set(contractNumber, site)
    })
  })
  return map
}

/**
 * Helper: Creates a map of plant IDs by plant number for fast lookup.
 */
const createPlantsByNumberMap = (plants: OrderIntakePlantDto[]): Map<string, string> => {
  const map = new Map<string, string>()
  plants.forEach((plant) => {
    map.set(plant.plantNumber, plant.plantId)
  })
  return map
}
